/**
 * The meta-game progress ops (package "progress"): crafting, the skill/perk editor, specialization, achievement /
 * task / daily-present / tribute claims, the grave search, the settings screen, worm names, the tutorial flag,
 * profile creation, start over and the session entry / time refresh.
 *
 * Client commands (RULE_OPS, every argument comes from the player's request):
 *   craft.assemble { stack }   craft.disassemble { item }                                   ./progress-craft.ts
 *   skills.commit { attack?, protect?, health?, worms?, perks?, slots? }   perks.reset   spec.choose { spec }
 *                                                                                            ./progress-skills.ts
 *   achiev.claim   task.claim                                                               ./progress-claims.ts
 *   time.refresh   progress.startOver   tutorial.close   names.set { names }
 *   settings.set { difficulty?, hardcore?, animation?, tzOffsetMin? }                       ./progress-profile.ts
 *
 * Server-run ops (SERVER_RULE_OPS, inputs the server owns; never reachable through player.command):
 *   present.claim { available }   tribute.claim { playerId, zones, clanMembers, serverTime? }
 *   grave.search { index, battle }                                                          ./progress-claims.ts
 *   profile.create { dev? } (input: emptyGameState())   session.enter { tzOffsetMin? }      ./progress-profile.ts
 */
import { claimOps, claimServerOps } from './progress-claims';
import { craftOps } from './progress-craft';
import { profileOps, profileServerOps } from './progress-profile';
import { skillsOps } from './progress-skills';

export { emptyGameState } from './progress-profile';

export const progressOps = {
  ...craftOps,
  ...skillsOps,
  ...claimOps,
  ...profileOps,
};

export const progressServerOps = {
  ...claimServerOps,
  ...profileServerOps,
};

/**
 * Reason codes the progress ops add to the well-known ones of ../errors.ts (RULE_REASONS: not_enough_gold,
 * not_enough_diam, level_too_low, limit_reached, not_owned, unknown_item, not_available, bad_args, bad_state).
 */
export const PROGRESS_REASONS = [
  /** skills.commit: a target value or perk level below the current one. */
  'cannot_decrease',
  'not_enough_skill_pts',
  'not_enough_perk_pts',
  /** skills.commit: a new perk without a free perk slot. */
  'no_free_slot',
  /** skills.commit: nothing to save (the editor's 'save' is shown only after a change). */
  'no_changes',
  /** grave.search: the grave was searched already. */
  'already_searched',
  /** profile.create on an account that has a profile. */
  'profile_exists',
  /** session.enter on an account without a profile (run profile.create first). */
  'no_profile',
  /** names.set: more names than editable fields (worms + 2). */
  'not_editable',
] as const;
